import './style.css';
import type { GraphData } from './types';
import { GraphSimulation } from './graph';
import type { SimNode } from './graph';
import { ZoomPanHandler } from './interactions';
import { ArticleRouter, renderWikiArticleContent } from './article';
import { initializeAgentsView } from './agentsView';
import { initOntologyView } from './ontology';
import { createCanvasContext } from './main/canvas';
import { initializeMainDom } from './main/dom';
import { attachMainHandlers } from './main/handlers';
import { initializeCommandPalette } from './main/palette';
import { createRenderLoop } from './main/render';
import { createRouter } from './main/router';
import { createTableRenderer } from './main/table';
import { createWikiRenderer } from './main/wiki';
import { createViewController } from './main/views';
import { createGraphViewHelpers } from './main/graphView';
import { initializeMainState } from './main/state';
import { createDetailPanelHelpers } from './main/detailPanel';
import { applyDataLoadWarning, createIssueIdResolver, loadGraphData } from './main/dataLoad';
import { createInteractionHandlers } from './main/interactionsSetup';
import { attachResetHandler, attachTabNavigation, createResizeCanvas } from './main/layoutControls';
import { normalizePathAliasesToHashRoutes } from './main/pathAliases';
import {
  drawArrow,
  drawDiamond,
  getCategoryColor,
  getCategoryKeys,
  getCommunityColor,
  getPrimitiveColor,
  getPrimitiveLabel,
  renderCommunityHulls,
  renderCommunityLabels,
} from './main/graphUtils';

let renderDetailPanel: (node: SimNode, data: GraphData) => string = () => '';
let attachDetailPanelHandlers: () => void = () => {};

async function main() {
  normalizePathAliasesToHashRoutes(window.location);
  console.log('🚀 Shadow Workipedia initializing...');

  // Load data
  const { data, dataLoadError, articlesReady } = await loadGraphData();
  let articlesPending = true;
  void articlesReady.finally(() => {
    articlesPending = false;
  });

  console.log(`📊 Loaded ${data.metadata.issueCount} issues, ${data.metadata.systemCount} systems`);
  if (data.metadata.articleCount) {
    console.log(`📚 ${data.metadata.articleCount} wiki articles available`);
  }

  const resolveIssueId = createIssueIdResolver(data);

  const {
    loading,
    warningBanner,
    graphView,
    tableView,
    wikiView,
    agentsView,
    agentsContainer,
    ontologyView,
    ontologyCanvas,
    ontologyLegend,
    wikiSidebarContent,
    wikiArticleContent,
    articleView,
    header,
    tabNav,
    filterBar,
    tooltip,
    detailPanel,
    panelContent,
    closeBtn,
  } = initializeMainDom();

  // Hide loading indicator
  if (loading) loading.classList.add('hidden');

  applyDataLoadWarning({ warningBanner, dataLoadError, data, protocol: window.location.protocol });

	  // Track selected wiki article
	  let selectedWikiArticle: string | null = null;
	  let selectedCommunity: string | null = null;
	  let wikiSection: 'articles' | 'communities' = 'articles';

	  // View state (needed early for router initialization)
	  let currentView: 'graph' | 'table' | 'wiki' | 'agents' | 'communities' | 'ontology' = 'graph';

	  // Forward declare render functions (implemented later)
	  let render: () => void = () => {};
	  let renderTable: () => void;
	  let renderWikiList: () => void;

	  // The agents view is initialised on first activation, not at startup: it
	  // pulls agent-priors.v1.json (~5 MB) and agent-data.pack, which the graph
	  // landing view never uses. Same lazy pattern as the ontology view below.
	  let agentsInitialized = false;

  // Store router reference for navigation
  let router: ArticleRouter;

  let ontologyInitialized = false;

  const viewController = createViewController({
    graphView,
    tableView,
    wikiView,
    agentsView,
    ontologyView,
    articleView,
    header,
    tabNav,
    filterBar,
    viewModeToggles: document.getElementById('view-mode-toggles'),
    categoryFilters: document.getElementById('category-filters'),
    clusterToggle: document.getElementById('cluster-toggle'),
    tabGraph: document.getElementById('tab-graph'),
    tabTable: document.getElementById('tab-table'),
    tabWiki: document.getElementById('tab-wiki'),
    tabAgents: document.getElementById('tab-agents'),
    tabOntology: document.getElementById('tab-ontology'),
    renderWikiList: () => renderWikiList(),
    onOntologyActivate: () => {
      if (!ontologyInitialized) {
        ontologyInitialized = true;
        initOntologyView(ontologyCanvas, ontologyLegend);
      }
    },
    onViewChange: (view) => {
      currentView = view;
      if (view === 'table') {
        renderTable();
      }
      if (view === 'agents' && !agentsInitialized) {
        agentsInitialized = true;
        initializeAgentsView(agentsContainer as HTMLElement);
      }
    },
  });

  const showView = viewController.showView;

  // Initialize article router (side effects only - registers hash change listener)
  router = createRouter({
    data,
    resolveIssueId,
    showView,
    renderWikiList: () => renderWikiList(),
    setSelectedWikiArticle: (value) => {
      selectedWikiArticle = value;
    },
    setSelectedCommunity: (value) => {
      selectedCommunity = value;
    },
    setWikiSection: (value) => {
      wikiSection = value;
    },
  });

  // Get canvas
  const { canvas, ctx } = createCanvasContext();

  // Transform state
  const initialState = initializeMainState();
  let {
    currentTransform,
    hoveredNode,
    selectedNode,
    connectedToSelected,
    showIssues,
    showSystems,
    showPrinciples,
    showPrimitives,
    showDataFlows,
    selectedPrimitive,
    showClusters,
    searchTerm,
    tableSortColumn,
    tableSortDirection,
  } = initialState;
  const { activeCategories, searchResults } = initialState;

  // Initialize simulation
  const graph = new GraphSimulation(data, canvas.width, canvas.height);

  const resizeCanvas = createResizeCanvas({
    canvas,
    graph,
    render: () => render(),
    getViewportSize: () => ({ width: window.innerWidth, height: window.innerHeight }),
    header: document.getElementById('header'),
    filterBar: document.getElementById('filter-bar'),
  });

  let zoomHandler: ZoomPanHandler | null = null;
  const { dragHandler, hoverHandler, clickHandler } = createInteractionHandlers({
    canvas,
    graph,
    data,
    tooltip,
    panelContent,
    detailPanel,
    render: () => render(),
    setSelectedNode,
    setHoveredNode: (node) => {
      hoveredNode = node;
    },
    getCurrentTransform: () => currentTransform,
    setCurrentTransform: (value) => {
      currentTransform = value;
    },
    getRenderDetailPanel: () => renderDetailPanel,
    getAttachDetailPanelHandlers: () => attachDetailPanelHandlers,
    syncZoomTransform: (transform) => {
      zoomHandler?.setTransform(transform);
    },
  });

  function recomputeConnectedToSelected() {
    connectedToSelected = new Set<string>();
    if (!selectedNode) return;

    const selectedId = selectedNode.id;
    for (const link of graph.getLinks()) {
      if (link.source.id === selectedId) {
        connectedToSelected.add(link.target.id);
      } else if (link.target.id === selectedId) {
        connectedToSelected.add(link.source.id);
      }
    }
  }

  function setSelectedNode(node: SimNode | null) {
    selectedNode = node;
    recomputeConnectedToSelected();
  }
  clickHandler.updateTransform(currentTransform);

  attachMainHandlers({
    graph,
    data,
    render,
    renderTable: () => renderTable(),
    detailPanel,
    closeBtn,
    setSelectedNode,
    getCurrentView: () => currentView,
    getShowIssues: () => showIssues,
    setShowIssues: (value) => {
      showIssues = value;
    },
    getShowSystems: () => showSystems,
    setShowSystems: (value) => {
      showSystems = value;
    },
    getShowPrinciples: () => showPrinciples,
    setShowPrinciples: (value) => {
      showPrinciples = value;
    },
    getShowPrimitives: () => showPrimitives,
    setShowPrimitives: (value) => {
      showPrimitives = value;
    },
    getShowDataFlows: () => showDataFlows,
    setShowDataFlows: (value) => {
      showDataFlows = value;
    },
    getSelectedPrimitive: () => selectedPrimitive,
    setSelectedPrimitive: (value) => {
      selectedPrimitive = value;
    },
    setShowClusters: (value) => {
      showClusters = value;
    },
    setSearchTerm: (value) => {
      searchTerm = value;
    },
    activeCategories,
    searchResults,
    getPrimitiveColor,
    getPrimitiveLabel,
  });

  // Initialize zoom/pan
  zoomHandler = new ZoomPanHandler(canvas, (transform) => {
    currentTransform = transform;
    hoverHandler.updateTransform(transform);
    clickHandler.updateTransform(transform);
    dragHandler.updateTransform(transform);
    render();
  });

  // Connect drag handler to zoom handler (so drag can disable pan)
  dragHandler.setZoomHandler(zoomHandler);

  const showSystemsBtn = document.getElementById('show-systems-btn') as HTMLButtonElement | null;
  const detailPanelHelpers = createDetailPanelHelpers({
    data,
    graph,
    panelContent,
    detailPanel,
    tooltip,
    canvas,
    router,
    getCurrentTransform: () => currentTransform,
    setCurrentTransform: (value) => {
      currentTransform = value;
    },
    hoverHandler,
    clickHandler,
    dragHandler,
    zoomHandler,
    render: () => render(),
    setSelectedNode,
    getShowSystems: () => showSystems,
    setShowSystems: (value) => {
      showSystems = value;
    },
    activateShowSystems: () => {
      if (showSystemsBtn) showSystemsBtn.classList.add('active');
    },
  });

  renderDetailPanel = detailPanelHelpers.renderDetailPanel;
  attachDetailPanelHandlers = detailPanelHelpers.attachDetailPanelHandlers;

  // Fit entire graph to view with padding
  const graphViewHelpers = createGraphViewHelpers({
    graph,
    canvas,
    getShowIssues: () => showIssues,
    getShowSystems: () => showSystems,
    getShowPrinciples: () => showPrinciples,
    activeCategories,
    setCurrentTransform: (value) => {
      currentTransform = value;
    },
    onTransformApplied: (transform) => {
      zoomHandler.setTransform(transform);
      hoverHandler.updateTransform(transform);
      clickHandler.updateTransform(transform);
      dragHandler.updateTransform(transform);
      render();
    },
  });

  const { fitToView, getVisibleNodes } = graphViewHelpers;

  // Render loop
  render = createRenderLoop({
    canvas,
    ctx,
    graph,
    data,
    hoverHandler,
    clickHandler,
    dragHandler,
    getVisibleNodes,
    getCurrentTransform: () => currentTransform,
    getShowClusters: () => showClusters,
    getShowDataFlows: () => showDataFlows,
    getSelectedNode: () => selectedNode,
    getHoveredNode: () => hoveredNode,
    getSearchTerm: () => searchTerm,
    searchResults,
    getConnectedToSelected: () => connectedToSelected,
    getSelectedPrimitive: () => selectedPrimitive,
    getPrimitiveColor,
    getCategoryColor,
    renderCommunityHulls,
    renderCommunityLabels,
    drawArrow,
    drawDiamond,
    fitToView,
  }).render;

  // Now that all dependencies (activeCategories, searchTerm, etc.) are initialized,
  // we can safely call resizeCanvas which calls render
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);

  const resetBtn = document.getElementById('reset-btn') as HTMLButtonElement | null;
  attachResetHandler({
    resetBtn,
    graph,
    fitToView,
    activeCategories,
    getCategoryKeys,
    getCategoryButtons: () => Array.from(document.querySelectorAll('.category-filter-btn')) as HTMLElement[],
    getSearchInput: () => document.getElementById('search') as HTMLInputElement | null,
    searchResults,
    setSearchTerm: (value) => {
      searchTerm = value;
    },
    setSelectedNode,
    detailPanel,
    render: () => render(),
  });

	  // Set up tab navigation
  const tabGraph = document.getElementById('tab-graph') as HTMLButtonElement;
  const tabTable = document.getElementById('tab-table') as HTMLButtonElement;
  const tabWiki = document.getElementById('tab-wiki') as HTMLButtonElement;
  const tabAgents = document.getElementById('tab-agents') as HTMLButtonElement;
  const tabOntology = document.getElementById('tab-ontology') as HTMLButtonElement;
  const tableContainer = document.getElementById('table-container') as HTMLDivElement;

  renderTable = createTableRenderer({
    graph,
    data,
    getShowIssues: () => showIssues,
    getShowSystems: () => showSystems,
    activeCategories,
    getSelectedPrimitive: () => selectedPrimitive,
    getSearchTerm: () => searchTerm,
    searchResults,
    getTableSortColumn: () => tableSortColumn,
    setTableSortColumn: (value) => {
      tableSortColumn = value;
    },
    getTableSortDirection: () => tableSortDirection,
    setTableSortDirection: (value) => {
      tableSortDirection = value;
    },
    getCategoryColor,
    tableContainer,
    setSelectedNode,
    panelContent,
    detailPanel,
    tooltip,
    renderDetailPanel,
    attachDetailPanelHandlers,
  });

  attachTabNavigation({
    tabGraph,
    tabTable,
    tabWiki,
    tabAgents,
    tabOntology,
    router,
    getSelectedNode: () => selectedNode,
    setSelectedNode,
    detailPanel,
  });

  initializeCommandPalette({
    router,
    graph,
    data,
    canvas,
    hoverHandler,
    clickHandler,
    dragHandler,
    zoomHandler,
    getCurrentTransform: () => currentTransform,
    getCurrentView: () => currentView,
    getShowIssues: () => showIssues,
    getShowSystems: () => showSystems,
    getShowPrinciples: () => showPrinciples,
    setSelectedNode,
    panelContent,
    detailPanel,
    tooltip,
    renderDetailPanel,
    attachDetailPanelHandlers,
    render,
    resetBtn,
  });

  // Wiki sidebar and article rendering
  const wikiSidebar = document.getElementById('wiki-sidebar');

  renderWikiList = createWikiRenderer({
    data,
    graph,
    router,
    resolveIssueId,
    getSelectedWikiArticle: () => selectedWikiArticle,
    getSelectedCommunity: () => selectedCommunity,
    getWikiSection: () => wikiSection,
    getArticlesPending: () => articlesPending,
    getCommunityColor,
    wikiSidebarContent,
    wikiArticleContent,
    wikiSidebar: wikiSidebar as HTMLDivElement | null,
    panelContent,
    detailPanel,
    canvas,
    getCurrentTransform: () => currentTransform,
    hoverHandler,
    clickHandler,
    dragHandler,
    zoomHandler,
    render,
    setSelectedNode,
    renderDetailPanel,
    renderWikiArticleContent,
    attachDetailPanelHandlers,
  });

  // Every renderer is wired, so run the initial route. The router was built
  // with deferInitialRoute, so this is the first time it fires — which is why
  // a deep link like #/wiki/<slug> or #/table now reaches a live renderer
  // instead of throwing past the rest of main().
  router.start();

  // The graph renders from graph.json alone; article prose arrives separately.
  // A deep link into the wiki can therefore land before the articles do, so
  // redraw those views once they are here. Graph, table, agents and ontology
  // read none of it and are left alone.
  void articlesReady.then(() => {
    const route = router.getCurrentRoute();
    const needsArticles =
      route?.kind === 'article' ||
      route?.kind === 'community' ||
      (route?.kind === 'view' && (route.view === 'wiki' || route.view === 'communities'));
    if (needsArticles) {
      renderWikiList();
    }
  });
}

main().catch(console.error);
